import { faCheck, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select as SelectMaterial, TextField } from '@material-ui/core';
import { Button } from '@vadiun/react-components';
import { MessageFormType, RulesConfigurationFormType } from 'app/traffic-lights-management/types/types';
import { useFormikContext } from 'formik';
import { ChannelType } from 'shared/models/ChannelTyoes';


const messageField = (color: MessageFormType | undefined, field: keyof MessageFormType) => {
    if (color == null || typeof color !== 'object') return field === 'channel_keys' ? [] : '';
    const value = color[field];

    if (field === 'channel_keys') {
        return Array.isArray(value) ? value : [];
    }
    return value ?? '';
};

interface MessagesConfigurationsModalProps {
    isOpen: boolean;
    onClose: () => void;
    index: number;
    readOnly: boolean;
    channels: ChannelType[];
}

const MessagesConfigurationsModal = ({ isOpen, onClose, index, readOnly, channels }: MessagesConfigurationsModalProps) => {
    const { values, setFieldValue } = useFormikContext<RulesConfigurationFormType>();
    const currentMessages = values.stages[index]?.semaphores?.[0];

    if (!currentMessages) {
        return null;
    }

    return (
        <Dialog
            key={`messages-configurations-modal-${index}`}
            open={isOpen}
            fullWidth
            maxWidth="sm"
            disableEscapeKeyDown
        >
            <DialogTitle>
                <p className="text-lg text-secondary-700">Configuración de mensajes del semáforo</p>
            </DialogTitle>
            <DialogContent dividers>
                <div className="flex flex-col gap-6 w-full py-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={`message-configuration-${i}`} className="flex w-full gap-2">
                            <div className={`rounded-full h-auto ${i === 0 ? 'bg-greenTrafficLight' : i === 1 ? 'bg-yellowTrafficLight' : i === 2 ? 'bg-orangeTrafficLight' : 'bg-redTrafficLight'}`} style={{ width: '8px' }} />
                            <div key={i} className="flex flex-col gap-2 w-full">
                                <FormControl variant="outlined"
                                    fullWidth>
                                    <InputLabel id="channel-label">Canal</InputLabel>
                                    <SelectMaterial
                                        disabled={readOnly}
                                        color="primary"
                                        value={currentMessages.semaphore_levels[i].semaphore_message_templates?.channel_keys || []} name={`stages[${index}].semaphores[0].semaphore_levels[${i}].semaphore_message_templates.channel_keys`} labelId="channel-label" label="Canal" multiple
                                        onChange={(e) => setFieldValue(`stages[${index}].semaphores[0].semaphore_levels[${i}].semaphore_message_templates.channel_keys`, e.target.value)}
                                        renderValue={(selected: any) => {
                                            if (selected.length === 0) {
                                                return <em>Seleccione un canal</em>;
                                            }
                                            return (
                                                <div className="grid grid-cols-2 gap-1" style={{ minHeight: 20 }}>
                                                    {channels.filter(({ key }) => (selected as string[])?.includes(key)).map(({ name, key }) => (
                                                        <span
                                                            key={key}
                                                            className="flex items-center gap-2 text-sm text-gray-800 bg-secondary-200 w-fit px-2 py-1 rounded-full"
                                                        >
                                                            {name}
                                                            {!readOnly && (
                                                                <button
                                                                    type="button"
                                                                    className="hover:text-red-500 font-bold leading-none"
                                                                    // Evitamos que el click abra/cierre el select
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const newValue = selected.filter(favKey => favKey !== key);
                                                                        setFieldValue(`stages[${index}].semaphores[0].semaphore_levels[${i}].semaphore_message_templates.channel_keys`, newValue);
                                                                    }}
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </span>
                                                    ))}
                                                </div>
                                            );
                                        }}
                                        MenuProps={{
                                            disableAutoFocusItem: true,
                                            getContentAnchorEl: null,
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 600,
                                                    backgroundColor: 'rgba(224, 242, 254, 1 )',
                                                    color: 'black',
                                                }
                                            }
                                        }}>
                                        {
                                            channels.map(({ key, name }) => (
                                                <MenuItem key={key} value={key}>
                                                    <div className="flex items-center w-full">
                                                        <p className="m-0">{name}</p>
                                                    </div>
                                                </MenuItem>
                                            ))
                                        }
                                    </SelectMaterial>
                                </FormControl>
                                <TextField
                                    name={`stages[${index}].semaphores[0].semaphore_levels[${i}].semaphore_message_templates.title`}
                                    label="Título"
                                    variant="outlined"
                                    className="bg-bgInputSection"
                                    fullWidth
                                    value={messageField(currentMessages.semaphore_levels[i].semaphore_message_templates, 'title')}
                                    onChange={(e) => setFieldValue(`stages[${index}].semaphores[0].semaphore_levels[${i}].semaphore_message_templates.title`, e.target.value)}
                                    disabled={readOnly}
                                />
                                <TextField
                                    name={`stages[${index}].semaphores[0].semaphore_levels[${i}].semaphore_message_templates.body`}
                                    label="Cuerpo"
                                    variant="outlined"
                                    className="bg-bgInputSection"
                                    fullWidth
                                    value={messageField(currentMessages.semaphore_levels[i].semaphore_message_templates, 'body')}
                                    onChange={(e) => setFieldValue(`stages[${index}].semaphores[0].semaphore_levels[${i}].semaphore_message_templates.body`, e.target.value)}
                                    disabled={readOnly}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </DialogContent>
            <DialogActions>
                <div className="flex w-full justify-between gap-2">
                    <Button variant="contained" color="red" onClick={() => {
                        if (readOnly) return;
                        Array.from({ length: 4 }).forEach((_, i) => {
                            setFieldValue(`stages[${index}].semaphores[0].semaphore_levels[${i}].semaphore_message_templates`, {
                                id: undefined,
                                channel_keys: [],
                                title: '',
                                body: '',
                            });
                        });
                    }} disabled={readOnly}>
                        <FontAwesomeIcon icon={faTrash} className='mr-2' />
                        Eliminar mensajes
                    </Button>
                    <Button variant="contained" color="primary" onClick={onClose}>
                        <FontAwesomeIcon icon={faCheck} className='mr-2' />
                        Cerrar Ventana de Mensajes
                    </Button>
                </div>
            </DialogActions>
        </Dialog >
    )
}

export default MessagesConfigurationsModal
